import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CompanyService } from '../../../../service/CompanyService';
import type { User } from '../../../../util/Types/User';
import { getUserInitials } from '../layoutBetaUtils';

interface UserCompany {
  id: string;
  name: string;
  logo?: string;
  isCompanyOwner?: boolean;
}

export interface AccountDisplay {
  avatarSrc: string | undefined;
  displayName: string;
  initials: string;
}

export function useAccountDisplay(authUser: User | null): AccountDisplay {
  const { data: companies = [] } = useQuery({
    queryKey: [
      'layoutBetaUserCompanies',
      authUser?.id,
      authUser?.selectedCompanyId,
    ],
    queryFn: async (): Promise<UserCompany[]> => {
      const res = await CompanyService.getUserCompanies();
      const list = res?.data;
      if (!Array.isArray(list)) {
        return [];
      }
      return list as UserCompany[];
    },
    enabled: Boolean(authUser?.id),
  });

  const { myCompany, selectedCompany } = useMemo(() => {
    const my = companies.find(c => c.isCompanyOwner) ?? null;
    let selected: UserCompany | null = null;
    if (authUser?.selectedCompanyId) {
      selected =
        companies.find(c => c.id === authUser.selectedCompanyId) ?? null;
    }
    return { myCompany: my, selectedCompany: selected };
  }, [companies, authUser?.selectedCompanyId]);

  return useMemo(() => {
    if (!authUser) {
      return {
        avatarSrc: undefined,
        displayName: '',
        initials: getUserInitials(null),
      };
    }

    const displayName = selectedCompany?.id
      ? selectedCompany.name
      : authUser.name ?? '';

    let avatarSrc: string | undefined;
    if (
      selectedCompany?.id &&
      myCompany?.id &&
      selectedCompany.id !== myCompany.id
    ) {
      avatarSrc = selectedCompany.logo;
    } else if (myCompany?.id) {
      avatarSrc = myCompany.logo;
    } else {
      avatarSrc = authUser.avatar;
    }

    return {
      avatarSrc,
      displayName,
      initials: getUserInitials(displayName || null),
    };
  }, [authUser, myCompany, selectedCompany]);
}
